import * as React from 'react';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SubscriptionRequiredBanner from '../../../components/SubscriptionRequiredBanner';
import SettingsSkeleton from '../components/SettingsSkeleton';
import IntegrationSettingsSection from '../components/IntegrationSettingsSection';
import TeamManagementSection from '../components/TeamManagementSection';
import NotificationSettingsSection from '../components/NotificationSettingsSection';
import { canManageNotificationSettings } from '../../../utils/roles';
import EmailNotificationPreferencesSection from '../components/EmailNotificationPreferencesSection';

const pageSx = {
  width: '100%',
  height: '86vh',
  overflowY: 'auto',
  overflowX: 'hidden',
};

const contentSx = {
  ml: 20,
  mr: 20,
  mt: 1,
  pb: 4,
};

export default function SettingsPage() {
  useDocumentTitle('Settings');

  const user = useSelector((state) => state.user.user);
  const subscription = useSelector((state) => state.subscription.subscription);
  const subscriptionLoading = useSelector((state) => state.subscription.loading);
  const isApiIntegrationEnabled = subscription?.features?.enable_api_integration;
  const isCustomSmtpEnabled = subscription?.features?.enable_custom_smtp;
  const isLoading = useSelector((state) => state.loader.isLoading);

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const tabFromUrl = searchParams.get('tab') || 'integration';
  const [activeTab, setActiveTab] = React.useState(tabFromUrl);
  const canAccessNotifications = canManageNotificationSettings(user);

  const handleTabChange = (_event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 'team') {
      setSearchParams({ tab: newValue, subtab: searchParams.get('subtab') || 'roles' });
    } else {
      setSearchParams({ tab: newValue });
    }
  };

  React.useEffect(() => {
    setActiveTab(tabFromUrl);
    if (tabFromUrl === 'team' && !user?.is_root_user) {
      navigate('/settings?tab=integration', { replace: true });
    }
    if (tabFromUrl === 'integration' && !user?.is_root_user) {
      navigate('/settings?tab=integration', { replace: true });
    }
    if (tabFromUrl === 'notifications' && !canAccessNotifications) {
      navigate('/settings?tab=integration', { replace: true });
    }
  }, [tabFromUrl, user?.is_root_user, canAccessNotifications, navigate]);

  if (isLoading || user == null || subscriptionLoading) {
    return <SettingsSkeleton />;
  }

  if (!user?.is_root_user) {
    return (
      <Box sx={pageSx}>
        <Box
          sx={{
            width: '100%',
            ml: 5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <SettingsOutlinedIcon sx={{ color: '#3741af' }} fontSize="large" />
          <Typography variant="h4">Settings</Typography>
        </Box>
        <SubscriptionRequiredBanner />
      </Box>
    );
  }

  const showIntegrationContent = isApiIntegrationEnabled;
  const isTeamTab = activeTab === 'team';
  const isNotificationsTab = activeTab === 'notifications' && canAccessNotifications;
  const tabsValue = activeTab === 'notifications' && !canAccessNotifications ? 'integration' : activeTab;
  const isAccountTab = activeTab === 'account';

  return (
    <Box sx={pageSx}>
      <Box sx={contentSx}>
        <Tabs value={tabsValue} onChange={handleTabChange} textColor="primary" indicatorColor="primary" sx={{ mb: 2 }}>
          <Tab value="integration" label="Integration Settings" />
          <Tab value="team" label="Team Management" />
          {canAccessNotifications && <Tab value="notifications" label="Notification Settings" />}
          <Tab value="account" label="Account Settings" />
        </Tabs>

        {activeTab === 'integration' &&
          (showIntegrationContent ? <IntegrationSettingsSection /> : <SubscriptionRequiredBanner />)}

        {isTeamTab && <TeamManagementSection />}

        {isNotificationsTab &&
          (isCustomSmtpEnabled ? (
            <NotificationSettingsSection />
          ) : (
            <SubscriptionRequiredBanner
              title="Custom email provider"
              description="Send notification emails through your own SMTP server or Microsoft 365."
            />
          ))}
        {isAccountTab && <EmailNotificationPreferencesSection />}
      </Box>
    </Box>
  );
}
